import { useEffect, useRef, useState, type ReactNode } from 'react';
import linksPhone from 'url:asset/launchpad/features/links-phone.webp';
import eventsIpad from 'url:asset/launchpad/features/events-ipad.jpg';
import worshipSongsScreen from 'url:asset/launchpad/features/worship-songs-screen.jpg';
import lavenderFrame from 'url:asset/launchpad/features/lavender-frame.webp';
import LaunchpadIntro from './LaunchpadIntro';

// Launchpad Features (Figma 3168:725): 311×346 cards, 18px apart, with back/forward controls
const CARD_W = 311
const CARD_GAP = 18

export default function LaunchpadSection() {
  const scrollerRef = useRef<HTMLDivElement>(null)
  const [canBack, setCanBack] = useState(false)
  const [canForward, setCanForward] = useState(false)

  // Keep the arrow states in sync with the scroll position and viewport width
  useEffect(() => {
    const scroller = scrollerRef.current
    if (!scroller) return

    const update = () => {
      setCanBack(scroller.scrollLeft > 1)
      setCanForward(scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 1)
    }

    update()
    scroller.addEventListener('scroll', update, { passive: true })
    const resizeObserver = new ResizeObserver(update)
    resizeObserver.observe(scroller)
    return () => {
      scroller.removeEventListener('scroll', update)
      resizeObserver.disconnect()
    }
  }, [])

  // Step to the next/previous card's snap position, so it never lands between cards
  const scrollByCard = (direction: 1 | -1) => {
    const scroller = scrollerRef.current
    if (!scroller) return
    const step = CARD_W + CARD_GAP
    const index = Math.round(scroller.scrollLeft / step) + direction
    scroller.scrollTo({ left: index * step, behavior: 'smooth' })
  }

  return <div className="py-16">
    <LaunchpadIntro />

    <div className="max-w-[1033px] mx-auto">
      <div
        ref={scrollerRef}
        className="overflow-x-auto snap-x snap-mandatory px-8 scroll-px-8 py-6 -my-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <div className="flex w-fit" style={{ gap: CARD_GAP }}>
          <FeatureCard
            title="Highlight important links."
            description="Place important links at the top of Launchpad, like a form to Get Connected, send a Prayer Request, or to Give."
          >
            <img src={linksPhone} className="absolute left-[39px] top-[12px] w-[232.819px] h-[475.985px] max-w-none" draggable={false} />
          </FeatureCard>

          <FeatureCard
            title="Automatic Updates."
            description="Your services, events, and playlists appear right on Launchpad, as soon as you make changes. And if you connect to Planning Center, new events automatically appear."
          >
            <img src={eventsIpad} className="absolute left-[6px] top-[3px] w-[299px] h-[341px]" draggable={false} />
          </FeatureCard>

          <FeatureCard
            title="Share Worship Songs."
            description="Salt finds worship songs across Apple Music, Spotify, YouTube Music, and Tidal. Wherever someone listens, they can save your worship songs."
          >
            <img
              src={worshipSongsScreen}
              className="absolute left-[50.1px] top-[-147.12px] w-[210.81px] h-[459.343px] max-w-none rounded-[40.934px]"
              draggable={false}
            />
            <img src={lavenderFrame} className="absolute left-[39px] top-[-156px] w-[233px] h-[475.985px] max-w-none" draggable={false} />
          </FeatureCard>
        </div>
      </div>

      {/* Only shown when the cards don't all fit */}
      {(canBack || canForward) && (
        <div className="flex justify-end gap-[4.5px] px-8 pr-[55px]">
          <ArrowButton direction="back" disabled={!canBack} onClick={() => scrollByCard(-1)} />
          <ArrowButton direction="forward" disabled={!canForward} onClick={() => scrollByCard(1)} />
        </div>
      )}
    </div>
  </div>
}

function FeatureCard(props: { title: string, description: string, children: ReactNode }) {
  return (
    <div className="shrink-0 snap-start" style={{ width: CARD_W }}>
      <div className="relative h-[346px] bg-white rounded-[28px] overflow-hidden shadow-[0px_4px_23.4px_0px_rgba(0,0,0,0.1)]">
        {props.children}
      </div>

      <p className="mt-3 px-[5px] w-[310px] min-h-[95px] text-[15px] leading-[1.3]">
        <span className="font-semibold">{props.title}</span>{"  "}
        <span className="text-[#888888]">{props.description}</span>
      </p>
    </div>
  )
}

function ArrowButton(props: { direction: 'back' | 'forward', disabled: boolean, onClick: () => void }) {
  return (
    <button
      onClick={props.onClick}
      disabled={props.disabled}
      aria-label={props.direction === 'back' ? 'Previous feature' : 'Next feature'}
      className="size-[25px] flex items-center justify-center rounded-full bg-[rgba(217,217,217,0.5)] text-[#555555] cursor-pointer transition-opacity duration-200 disabled:opacity-25 disabled:cursor-default"
    >
      <svg
        viewBox="0 0 8 14"
        className="h-[11px] w-auto"
        style={{ transform: props.direction === 'back' ? 'scaleX(-1)' : undefined }}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M1.5 1.5 6.5 7l-5 5.5" />
      </svg>
    </button>
  )
}
